import { RoutePreview } from "@mapmycustomers/shared/types/entity/Route";

import { EditorRouteStop } from "../types/RouteStop";

import { isManualStop } from "./stopPredicates";

export interface ManualPreviewMatch {
  arrivalTime?: string;
  previewIndex: number;
  scheduledTime?: null | string;
  scheduledTimeSkipped?: boolean;
  travelDistance?: number;
  travelTime?: number;
}

/**
 * Pairs editor manual stops (entity === null, in array order) to preview manual stops
 * (sorted by originalIndex). Keys are manual-stop pivot ids WITHIN the passed `stops`
 * array — pass one coherent stop list (editor stops OR a loaded route's stops), never a
 * merged population.
 *
 * Inclusion is decided by the shared `isManualStop` predicate — the same one the send side
 * (collectCreateStopItems / getAppendStopsOperations) uses, so the positional pairing can't
 * drift between the two.
 */
const correlateManualStopsToPreview = (
  stops: ReadonlyArray<EditorRouteStop>,
  previewStops: RoutePreview["stopTime"] | undefined
): Map<number, ManualPreviewMatch> => {
  const result = new Map<number, ManualPreviewMatch>();
  if (!previewStops) {
    return result;
  }
  const manualPreview = previewStops
    .map((previewStop, previewIndex) => ({ previewIndex, previewStop }))
    .filter(({ previewStop }) => previewStop.entity === null)
    .sort((left, right) => left.previewStop.originalIndex - right.previewStop.originalIndex);

  let cursor = 0;
  stops.forEach((stop) => {
    if (!isManualStop(stop)) {
      return;
    }
    const match = manualPreview[cursor];
    cursor += 1;
    if (!match) {
      return;
    }
    result.set(stop.id, {
      arrivalTime: match.previewStop.arrivalTime,
      previewIndex: match.previewIndex,
      scheduledTime: match.previewStop.scheduledTime,
      scheduledTimeSkipped: match.previewStop.scheduledTimeSkipped,
      travelDistance: match.previewStop.travelDistance,
      travelTime: match.previewStop.travelTime,
    });
  });
  return result;
};

export default correlateManualStopsToPreview;
